import { Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { CTAFooter } from '../components/CTAFooter';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 lowercase">

          <h1 className="text-3xl font-bold text-foreground mb-2">Terms of Service</h1>
          <p className="text-muted-foreground mb-8"><strong>Effective Date:</strong> April 5, 2026</p>

          <p className="text-muted-foreground leading-relaxed mb-6">
            Please read these Terms of Service ("Terms") carefully before using the Discgo platform
            operated by Discgo LLC ("Discgo," "we," "us," or "our"), accessible at www.godiscgo.com.
            By accessing or using the Service, you agree to be bound by these Terms. If you do not agree,
            you may not use the Service.
          </p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">1. About Discgo</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Discgo is an online booking platform that connects disc golf course owners ("Course Owners")
              with disc golf players ("Players"). Discgo facilitates bookings and collects a booking fee for
              each transaction. Use of the platform is free for both Course Owners and Players, except for
              applicable booking fees.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">2. Eligibility</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You must be at least 18 years of age to create an account or use the Service. By using the
              Service, you represent and warrant that you meet this requirement. If you are using the Service
              on behalf of a business entity, you represent that you have the authority to bind that entity
              to these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">3. Accounts</h2>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">3.1 Registration</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              To access certain features of the Service, you must create an account. You agree to provide
              accurate, current, and complete information during registration and to keep your account
              information up to date.
            </p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">3.2 Account Security</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You are responsible for maintaining the confidentiality of your account credentials and for
              all activity that occurs under your account. You agree to notify us immediately at{' '}
              <a href="mailto:discgoinfo@gmail.com" className="text-primary hover:underline">discgoinfo@gmail.com</a>{' '}
              if you suspect any unauthorized use of your account. Discgo is not liable for any loss or
              damage arising from unauthorized account access resulting from your failure to safeguard
              your credentials.
            </p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">3.3 Account Termination</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We reserve the right to suspend or terminate your account at our discretion, with or without
              notice, if we believe you have violated these Terms or engaged in conduct harmful to Discgo,
              other users, or third parties.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">4. Booking and Fees</h2>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">4.1 Platform Role</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Discgo acts solely as an intermediary platform. We do not own, operate, manage, or control
              any disc golf courses listed on the Service. Course Owners are solely responsible for the
              accuracy of their listings and the services they provide.
            </p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">4.2 Booking Fee</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Discgo charges a booking fee on each transaction processed through the platform. This fee is
              disclosed at the time of booking. The booking fee is non-refundable except as expressly stated
              in these Terms or required by applicable law.
            </p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">4.3 Payment Processing</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              All payments are processed through Stripe, our third-party payment processor. By making a
              payment through the Service, you agree to Stripe's Terms of Service and authorize Stripe to
              charge your selected payment method. Discgo does not store your full payment card details.
            </p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">4.4 Cancellations and Refunds</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Cancellation and refund policies for specific bookings are set by the Course Owner. Please
              review the cancellation policy before completing a booking. Discgo's booking fee may not be
              refundable in the event of a cancellation. Any disputes regarding the services provided by a
              Course Owner should be resolved directly with the Course Owner. Discgo may, at its sole
              discretion, assist in resolving disputes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">5. User Conduct</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">By using the Service, you agree not to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4 text-muted-foreground">
              <li>Use the Service for any unlawful purpose or in violation of any applicable laws or regulations</li>
              <li>Provide false, misleading, or inaccurate information in your account or listings</li>
              <li>Engage in fraudulent bookings, chargebacks, or payment disputes in bad faith</li>
              <li>Harass, abuse, threaten, or harm other users of the Service</li>
              <li>Attempt to gain unauthorized access to any part of the Service or another user's account</li>
              <li>Use automated tools (bots, scrapers, crawlers) to access or extract data from the Service</li>
              <li>Post or transmit any content that is defamatory, obscene, or infringes on the intellectual property rights of others</li>
              <li>Interfere with or disrupt the integrity or performance of the Service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">6. Course Owner Responsibilities</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">Course Owners who list their courses on the Discgo platform agree to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4 text-muted-foreground">
              <li>Provide accurate and up-to-date information about their courses, availability, and pricing</li>
              <li>Honor confirmed bookings made through the platform</li>
              <li>Comply with all applicable local, state, and federal laws in operating their facilities</li>
              <li>Maintain appropriate insurance and safety standards for their facilities</li>
              <li>Handle customer service issues relating to their course and services</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">7. Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              All content, trademarks, logos, and materials on the Service, including but not limited to
              the Discgo name, logo, and platform design, are the property of Discgo LLC or its licensors
              and are protected by applicable intellectual property laws. You may not use, reproduce, or
              distribute any such materials without our prior written consent.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You retain ownership of any content you submit to the Service (such as course listings or
              profile information). By submitting content, you grant Discgo a non-exclusive, royalty-free,
              worldwide license to use, display, and reproduce that content in connection with operating
              and promoting the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">8. Disclaimers</h2>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">8.1 No Warranty</h3>
            <p className="uppercase text-muted-foreground leading-relaxed mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
              FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. DISCGO DOES NOT WARRANT THAT THE
              SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
            </p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">8.2 Third-Party Services</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Discgo is not responsible for the actions, content, or services of Course Owners or any third
              parties. Your interactions with Course Owners are solely between you and the Course Owner.
              Discgo assumes no liability for disputes, injuries, losses, or damages arising from your use
              of a disc golf course booked through the platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">9. Limitation of Liability</h2>
            <p className="uppercase text-muted-foreground leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, DISCGO LLC AND ITS OFFICERS, DIRECTORS,
              EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR
              GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, EVEN IF DISCGO HAS BEEN
              ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>
            <p className="uppercase text-muted-foreground leading-relaxed mb-4">
              IN NO EVENT SHALL DISCGO'S TOTAL LIABILITY TO YOU EXCEED THE GREATER OF (A) THE TOTAL BOOKING
              FEES PAID BY YOU TO DISCGO IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED
              U.S. DOLLARS ($100).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">10. Indemnification</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You agree to indemnify, defend, and hold harmless Discgo LLC and its officers, directors,
              employees, and agents from and against any claims, liabilities, damages, losses, and expenses
              (including reasonable attorneys' fees) arising out of or related to: (a) your use of the
              Service; (b) your violation of these Terms; (c) your violation of any rights of another person
              or entity; or (d) any content you submit to the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">11. Governing Law and Dispute Resolution</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              These Terms are governed by the laws of the State of Colorado, without regard to its conflict
              of law provisions. Any disputes arising from or related to these Terms or the Service shall
              first be attempted to be resolved through good-faith negotiation. If a dispute cannot be
              resolved informally, it shall be submitted to binding arbitration in accordance with the rules
              of the American Arbitration Association, conducted in Colorado.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Notwithstanding the foregoing, either party may seek injunctive or other equitable relief in
              any court of competent jurisdiction. You agree that any claims must be brought in your
              individual capacity and not as a plaintiff or class member in any purported class action
              proceeding.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">12. Modifications to the Terms</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Discgo reserves the right to modify these Terms at any time. When we make material changes,
              we will update the effective date at the top of this document and may notify you by email or
              through a notice on the Service. Your continued use of the Service after changes become
              effective constitutes your acceptance of the revised Terms. If you do not agree to the updated
              Terms, you must stop using the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">13. Course Distribution Fund</h2>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">13.1 Discretionary Nature</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Discgo may, at its sole and absolute discretion, establish and maintain a Course Distribution Fund
              (the "Fund"). The Fund, including its existence, structure, contribution rate, distribution
              methodology, and payment schedule, is subject to change or discontinuation at any time and for any
              reason, with or without notice. Participation in the Fund does not constitute a contractual right or
              entitlement, and Discgo makes no representations or warranties regarding the amount, timing, or
              continuity of any distributions.
            </p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">13.2 Contribution Rate</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              When active, the Fund is funded by a percentage of Discgo's platform revenue as determined by
              Discgo in its sole discretion. The current contribution rate, if any, will be published in Discgo's
              platform documentation and may be updated at any time.
            </p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">13.3 Distribution</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Fund distributions, if any, are made to eligible participating courses on a periodic basis as
              determined by Discgo. Discgo reserves the right to modify the distribution methodology, frequency,
              and per-course calculation at any time.
            </p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">13.4 Eligibility</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              To be eligible for Fund distributions, a course must (a) maintain an active account in good
              standing, (b) be in full compliance with these Terms of Service, and (c) meet any minimum activity
              thresholds established by Discgo from time to time. Discgo reserves the right to disqualify any
              course from Fund participation at its sole discretion.
            </p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">13.5 Minimum Distribution Threshold</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Discgo may establish a minimum distribution amount below which payments will be held and rolled into
              the following distribution period. Discgo is not obligated to distribute amounts below any such
              threshold within any given period.
            </p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">13.6 Non-Transferability</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Any interest a course may have in the Fund is non-transferable and may not be assigned, pledged,
              or otherwise conveyed to any third party.
            </p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">13.7 Taxes</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Course operators are solely responsible for all taxes, withholdings, and reporting obligations
              arising from any Fund distributions received. Discgo does not provide tax advice and makes no
              representations regarding the tax treatment of Fund distributions.
            </p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">13.8 No Waiver</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Discgo's failure to exercise any right under this Section shall not constitute a waiver of such right.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">14. Miscellaneous</h2>
            <ul className="list-disc pl-6 space-y-2 mb-4 text-muted-foreground">
              <li><strong className="text-foreground">Entire Agreement:</strong> These Terms, together with our{' '}
                <Link to="/privacy-policy" className="text-primary hover:underline">privacy policy</Link>,
                constitute the entire agreement between you and Discgo regarding the Service.</li>
              <li><strong className="text-foreground">Severability:</strong> If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.</li>
              <li><strong className="text-foreground">Waiver:</strong> Discgo's failure to enforce any right or provision of these Terms will not be deemed a waiver of such right or provision.</li>
              <li><strong className="text-foreground">Assignment:</strong> You may not assign your rights or obligations under these Terms without our prior written consent. Discgo may assign these Terms without restriction.</li>
              <li><strong className="text-foreground">No Agency:</strong> Nothing in these Terms creates a partnership, joint venture, agency, or employment relationship between you and Discgo.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">15. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">If you have any questions about these Terms, please contact us at:</p>
            <address className="not-italic text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Discgo LLC</strong><br />
              <a href="https://www.godiscgo.com" className="text-primary hover:underline">www.godiscgo.com</a><br />
              <a href="mailto:discgoinfo@gmail.com" className="text-primary hover:underline">discgoinfo@gmail.com</a>
            </address>
          </section>

        </div>
      </main>
      <CTAFooter />
    </div>
  );
}
